import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { RadioButton, RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import * as React from 'react'
import { toSnakeCase } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import {
  FilterContainer,
  FilterItemOption,
  FilterItemOptionContent,
  FilterItemOptionControl,
  FilterItemOptionTrailing,
  FilterItemOptionWrapper,
  FilterItemsContainer,
  FilterLabel,
  FilterOptionPrice,
  FilterOptionValue,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/Filters'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { updateSingleValueFilter } from '@/scopes/checkout/filterableCars/domain/actions'
import { useFilter } from '@/scopes/checkout/filterableCars/domain/hooks'
import { CarFilterType } from '@/scopes/checkout/filterableCars/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

type FuelFilterType = 'all_cars' | 'diesel' | 'gasoline' | 'electric'

function filterTranslationTextKeys(key: FuelFilterType): string | undefined {
  const mapping: Record<string, string> = {
    all_cars: 'checkout_steps_ancillaries_car_rental_filtering_filters_fuel_type_all_cars_text',
    diesel: 'checkout_steps_ancillaries_car_rental_filtering_filters_fuel_type_diesel_text',
    gasoline: 'checkout_steps_ancillaries_car_rental_filtering_filters_fuel_type_gasoline_text',
    electric: 'checkout_steps_ancillaries_car_rental_filtering_filters_fuel_type_electric_text',
  }
  return mapping[key]
}

export function FuelTypeFilter() {
  const { filter, setFilterValue } = useFilter(CarFilterType.FuelType)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const handleFilterValueChange = React.useCallback(
    (value: string) => {
      if (!value)
        return
      if (!filter)
        return
      setFilterValue(updateSingleValueFilter({ value })(filter))

      sendEvent(
        createCarEvent({
          result: 'Filter selected',
          type: value,
          description: 'Fuel Type',
          element: 'radiobutton',
          validated: true,
        }),
      )
    },
    [sendEvent, filter, setFilterValue],
  )

  if (!filter)
    return null

  const { filterOptions, selectedFilter } = filter

  return (
    <FilterContainer>
      <FilterLabel>{intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_fuel_type_label` })}</FilterLabel>
      <RadioButtonGroup value={selectedFilter ?? undefined} onValueChange={handleFilterValueChange}>
        <FilterItemsContainer>
          {filterOptions.map((option) => {
            const { value, name, quantity, startingPrice } = option
            const filterTextKey = filterTranslationTextKeys(toSnakeCase(name) as FuelFilterType) || ''

            return (
              <FilterItemOptionWrapper key={value}>
                <ListItem variant="outlined">
                  <FilterItemOption>
                    <FilterItemOptionControl>
                      <RadioButton value={value} />
                    </FilterItemOptionControl>
                    <FilterItemOptionContent>
                      <FilterOptionValue>
                        {filterTextKey ? intl.formatMessage({ id: filterTextKey }) : name}
                        {' '}
                        (
                        {quantity ?? 0}
                        )
                      </FilterOptionValue>
                    </FilterItemOptionContent>
                    {startingPrice !== undefined && startingPrice !== null && (quantity && quantity > 0)
                      ? (
                          <FilterItemOptionTrailing>
                            <FilterOptionPrice>
                              {intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_from_starting_price_text` }, { price: intl.formatTruncatedCurrency(startingPrice) })}
                            </FilterOptionPrice>
                          </FilterItemOptionTrailing>
                        )
                      : ''}
                  </FilterItemOption>
                </ListItem>
              </FilterItemOptionWrapper>
            )
          })}
        </FilterItemsContainer>
      </RadioButtonGroup>
    </FilterContainer>
  )
}
